import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import { App } from '@/App'
import { TooltipProvider } from '@/components/ui/overlay'
import { ApiError } from '@/lib/api'
import { AuthProvider } from '@/providers/auth'
import { PreviewProvider } from '@/providers/preview'
import { ThemeProvider } from '@/providers/theme'
import '@/styles.css'

// After a deploy, a tab opened earlier still asks for the previous build's code
// chunks, which no longer exist. Reload once to pick up the new build instead of
// failing; the timestamp stops a broken deploy from reloading in a loop.
window.addEventListener('vite:preloadError', (event) => {
  const key = 'sbt:reloaded-for-new-build'
  let last = 0
  try {
    last = Number(sessionStorage.getItem(key) ?? 0)
    sessionStorage.setItem(key, String(Date.now()))
  } catch {
    // Storage unavailable: reload anyway, once per page load.
  }
  if (Date.now() - last > 60_000) {
    event.preventDefault()
    window.location.reload()
  }
})

// design.md §P24: when a change is made — in a dialog or anywhere, done or partly done
// — everything on screen is read again in the background, the page behind the dialog
// included. Each keeps showing what it has until the new data arrives, so its filters,
// scroll, tabs and selection stay. A mutation that only reads (a dry run) says so with
// `meta: { reads: true }`; nothing else needs to say what to refresh.
const queryClient: QueryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSettled: (_data, _error, _variables, _context, mutation) => {
      if (mutation.meta?.reads) return
      void queryClient.invalidateQueries()
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Permission and auth failures will not succeed on a retry.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
        return failureCount < 2
      },
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <TooltipProvider delayDuration={300}>
              <PreviewProvider>
                <App />
              </PreviewProvider>
            </TooltipProvider>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
)
